import {useMutation} from '@github-ui/react-query'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useDismissibleErrorBanner} from './use-dismissible-error-banner'

interface DemoRepositoryResponse {
  redirectUrl?: string
  message?: string
}

async function createDemoRepository(path: string): Promise<string> {
  const response = await verifiedFetch(path, {
    method: 'POST',
    headers: {Accept: 'application/json'},
  })

  if (response.redirected) return response.url

  const result = (await response.json()) as DemoRepositoryResponse
  if (!response.ok) throw new Error('The demo repository could not be created.')
  if (!result.redirectUrl) throw new Error('The demo repository response did not include a destination.')

  return result.redirectUrl
}

/**
 * Creates the onboarding demo repository and reports failures through BannerStack.
 */
export function useCreateDemoRepository(path: string) {
  const {dismissCurrentBanner, showErrorBanner} = useDismissibleErrorBanner('Unable to create demo repository')
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => createDemoRepository(path),
    onMutate: dismissCurrentBanner,
    onSuccess: redirectUrl => navigate(redirectUrl),
    onError: () => showErrorBanner(),
  })
}
